import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import BinaryStateButton from "components/BinaryStateButton";
import { BreadcrumbProps, Breadcrumbs } from "components/Breadcrumbs/Breadcrumbs";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import PageHeader from "components/Layout/PageHeader";
import { UnsavedChangesModal } from "components/Prompt/UnsavedChangesModal";
import { singleCustomObjectProvisioningStateSelector } from "components/ProvisioningStateDot/ProvisioningState.selectors";
import { ProvisioningStateData } from "components/ProvisioningStateDot/ProvisioningStateDot";
import RecordLabel from "components/RecordLabel";
import RoleSelect from "components/RoleSelect";
import StatusBadge from "components/StatusBadge";
import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import OverflowTooltip from "components/Tooltip/OverflowTooltip";
import Tooltip from "components/Tooltip/Tooltip";
import { resourceByName } from "config/resources";
import { ActivityConfig, FeConfigType, RelationshipConfig, ResourceConfig } from "config/config";
import { getIsReadonly } from "config/resourceUtils";
import { getCustomObjectProvisioningStateColors } from "config/ts-utils";
import { ResourcePageHeaderIconForm } from "containers/Resources/components/ResourcePageHeaderIconForm/ResourcePageHeaderIconForm";
import { ResourcePageHeaderDescriptionForm } from "containers/Resources/components/ResourcesPageHeaderDescriptionForm/ResourcesPageHeaderDescriptionForm";
import { ResourcePageHeaderTitleForm } from "containers/Resources/components/ResourcesPageHeaderTitleForm/ResourcesPageHeaderTitleForm";
import { GitAction } from "containers/Resources/screens/ResourcesSingle/components/GitAction/GitAction";
import {
    isContainerType,
    UncommittedDataGroupedByName,
} from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/utils";
import ResourceStats from "containers/Resources/screens/ResourcesSingle/components/ResourceStats";
import { DetailsPopover } from "containers/Resources/screens/ResourcesSingle/components/ResourcesSinglePageHeader/DetailsPopover";
import { HiddenActionsComponent } from "containers/Resources/screens/ResourcesSingle/components/ResourcesSinglePageHeader/HiddenActionsComponent";
import { PinPopover } from "containers/Resources/screens/ResourcesSingle/components/ResourcesSinglePageHeader/PinPopover";
import {
    getBootstrapUserBadge,
    getBundleContentBadge,
    getDeletedBadges,
    getDeprecationBadge,
    getPendingDeleteBadge,
    getPendingRestoreBadge,
    getPendingTrashBadge,
    getReadonlyBadges,
    getRecordContainer,
    getSyntaxErrorBadge,
} from "containers/Resources/screens/ResourcesSingle/utils";
import { checkBootstrapUser } from "containers/Workspaces/utils";
import { isEmpty } from "lodash";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useFormState } from "react-final-form";
import { SubmitLock } from "components/Form/components/SubmitLock/SubmitLock";
import { shallowEqual, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { customObjectCanvasPathFragment } from "utils/RouterManager/constants";
import { mapScreensToPaths } from "utils/RouterManager/utils";
import { getCanvasLink, getRelativeUrl } from "utils/service";
import { useSticky } from "utils/useSticky";
import { getLeadingSpaces, getTrailingSpaces } from "utils/utils";
import { AccessList } from "../AccessList/AccessList";
import { ZoneTagList } from "../ZoneTagList/ZoneTagList";
import "./ResourcesSinglePageHeader.scss";

const recordDocLinks = {
    connector: "connection-resource",
    custom_object: "object-templates-custom-objects#custom-objects",
    dashboard: "dashboards",
    object_template: "object-templates-custom-objects#object-templates",
    schedule: "scheduling#schedule",
    scheduler: "scheduling#scheduler",
    setting: "settings-and-locks",
    schema: "schema-creation-and-validation#schema-resource",
    bundle_repository: "bundles#bundle-repository",
    ldap_config: "ldap-integration",
    role: "rbac",
    sync_config: "workspace-linking",
    devolutions_config: "vault-integration#devolutions-server",
    vault_config: "vault-integration#hashicorp-vault",
    flow: "flows",
    file: "files",
    project: "projects",
    bundle: "bundles",
    webhook: "webhooks",
    wrapper: "wrappers",
    plugin: "plugins",
};

interface ResourceSinglePageHeaderProps {
    editIcon?: boolean;
    /** Enable the curated built-in-icon picker alongside upload (opt-in per resource). */
    curatedIcons?: boolean;
    /**
     * Docs page (e.g. "/docs/dashboards") for this record type. When set, an
     * existing record's header shows a "?" help icon next to the breadcrumbs.
     */
    docsUrlPath?: string;
    editName?: boolean;
    showDescription?: boolean;
    editDescription?: boolean;
    handleSubmit: () => Promise<any>;
    pristine: boolean;
    resourceData?: any;
    resourceName: FeConfigType;
    fetchResourceData: () => void;
    /**
     * Optional manual-refresh handler for the header Refresh button. When provided
     * (static single-record screens), renders a Refresh IconButton that re-fetches
     * the record and shows current server state. Omitted for live screens (e.g.
     * execution) and new-record create screens. See
     * operator-task-fe-single-record-screen-refresh-button.
     */
    onRefresh?: () => void;
    isRefreshing?: boolean;
    resetForm: () => void;
    backButtonLink?: any;
    resourceConfig: ResourceConfig | ActivityConfig | RelationshipConfig;
    defaultValues?: any;
    checkIfNameExists?: (e) => void;
    titleIcon?: string;
    isDisabled?: boolean;
    fetchSearchTabs?: () => Promise<any>;
    isNewRecord?: boolean;
    newRecordLocationData?: {
        id: string;
        type: "project" | "bundle" | "workspace";
        name: string;
        icon: any;
    };
    uncommittedDataByPath?: UncommittedDataGroupedByName;
    hasValidationErrors?: boolean;
    isGitLocked?: boolean;
    isFetching?: boolean;
    isGitEnabled?: boolean;
    bootstrapUserName?: string;
    iri?: string;
}

const ResourceSinglePageHeader = ({
    editIcon = false,
    curatedIcons = false,
    docsUrlPath,
    editName = true,
    showDescription = true,
    editDescription = true,
    children,
    handleSubmit,
    pristine,
    resourceData,
    resourceName,
    fetchResourceData,
    onRefresh,
    isRefreshing,
    resetForm,
    backButtonLink,
    resourceConfig,
    defaultValues,
    checkIfNameExists,
    titleIcon,
    isDisabled,
    fetchSearchTabs,
    isNewRecord,
    newRecordLocationData,
    uncommittedDataByPath = {},
    hasValidationErrors,
    isGitLocked,
    isFetching,
    isGitEnabled,
    bootstrapUserName,
    iri,
}: React.PropsWithChildren<ResourceSinglePageHeaderProps>) => {
    const [statsPopoverEl, setStatsPopoverEl] = useState(null);
    const [accessListPopoverEl, setAccessListPopoverEl] = useState(null);
    const [zonePopoverEl, setZonePopoverEl] = useState(null);

    const [isModalVisible, setModalVisible] = useState(false);

    const applyAction = useRef<() => void>();

    const projectOrBundleId = resourceData?.project_id ?? resourceData?.bundle_id;
    const recordId = resourceData?.id;

    const isReadOnly = getIsReadonly(resourceConfig, resourceData);
    // The header's icon, name and description are fields of the record form:
    // lock them while a save is in flight (ui-design-principle-modals-dialogs).
    const { submitting } = useFormState({ subscription: { submitting: true } });

    const pageHeaderIcon = (
        <ResourcePageHeaderIconForm
            handleSubmit={handleSubmit}
            recordId={recordId}
            isReadOnly={isReadOnly}
            enableCuratedPicker={curatedIcons}
        />
    );

    const canEditTitle = editName && !isReadOnly && !resourceData?.deleted_at;

    const pageHeaderTitle = canEditTitle ? (
        <SubmitLock locked={submitting}>
            <ResourcePageHeaderTitleForm
                defaultValue={defaultValues?.["name"]}
                checkIfNameExists={checkIfNameExists}
                isFetching={isFetching}
            />
        </SubmitLock>
    ) : (
        <OverflowTooltip overlay={resourceData?.name} isOverlapping={false}>
            <span className="text-xl text-bold">
                <span className="space-background">{getLeadingSpaces(resourceData?.name)}</span>
                {resourceData?.name?.trim() ?? ""}
                <span className="space-background">{getTrailingSpaces(resourceData?.name)}</span>
            </span>
        </OverflowTooltip>
    );

    const getPageHeaderSubtitle = () => {
        if (showDescription) {
            return (
                <SubmitLock locked={submitting}>
                    <ResourcePageHeaderDescriptionForm
                        defaultValue={defaultValues?.["description"]}
                        isReadOnly={!editDescription || isReadOnly || resourceData?.deleted_at}
                    />
                </SubmitLock>
            );
        }
        return null;
    };
    const pageHeaderSubtitle = getPageHeaderSubtitle();

    const { ref, isSticky } = useSticky<HTMLDivElement>();
    const canvasType =
        resourceName === "custom_object" ? customObjectCanvasPathFragment : undefined;
    const canvasLink =
        resourceName === "object_template" || resourceName === "custom_object"
            ? getCanvasLink(
                  resourceData?.project_id ? "project" : "bundle",
                  projectOrBundleId,
                  canvasType,
                  "id",
                  // Deep-link the opened record as the canvas focus target so the
                  // canvas highlights + centers on it (opened from this record).
                  recordId,
              )
            : getCanvasLink(resourceName, recordId, canvasType);

    // Whether the content is tall enough that the header should stick on scroll.
    // Reading form/header scrollHeight forces a synchronous layout reflow, so we
    // do it in an effect (on mount, resize, data change, and whenever the form
    // content actually resizes) instead of on every render.
    const [isScrollNeeded, setIsScrollNeeded] = useState(false);

    useLayoutEffect(() => {
        const computeScrollNeeded = () => {
            const form = document?.getElementsByTagName("form")?.[0];
            const header = document?.getElementsByClassName("page-header--form")?.[0];
            if (!form) {
                setIsScrollNeeded(false);
                return;
            }
            const headerScrollHeight = header?.scrollHeight ?? 0;
            // 80 - topbar height
            // 109 - sticky header min height
            const scrollHeightWithStickyHeader = 80 + form.scrollHeight - headerScrollHeight + 109;
            setIsScrollNeeded(scrollHeightWithStickyHeader > window.innerHeight);
        };

        computeScrollNeeded();
        window.addEventListener("resize", computeScrollNeeded);

        const form = document?.getElementsByTagName("form")?.[0];
        let resizeObserver: ResizeObserver | undefined;
        if (form && typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(computeScrollNeeded);
            resizeObserver.observe(form);
        }

        return () => {
            window.removeEventListener("resize", computeScrollNeeded);
            resizeObserver?.disconnect();
        };
    }, [resourceData, isSticky]);

    const provisioningStateSelector = useMemo(() => {
        if (resourceName === "custom_object") {
            return singleCustomObjectProvisioningStateSelector(recordId);
        }
        return () => null;
    }, [resourceName, recordId]);

    const provisioningState: ProvisioningStateData | undefined = useSelector(
        provisioningStateSelector,
        shallowEqual,
    );

    // A custom object should ALWAYS show a badge with its current provisioning
    // state. The live websocket map (provisioningState) only carries COs that are
    // actively provisioning, so a READY / DELETED / *_FAILED record would show no
    // badge at all — fall back to the value loaded onto the record itself. The
    // live map still takes precedence so the badge updates in real time.
    const provisioningStateValue =
        resourceName === "custom_object"
            ? (provisioningState?.provisioning_state ?? resourceData?.provisioning_state)
            : undefined;

    const isBootstrapUser = checkBootstrapUser({
        name: resourceData?.name,
        bootstrapUserName,
        recordType: resourceData?.record_type,
    });

    const pageHeaderAdditionalActions = isNewRecord ? null : (
        <>
            {resourceData?.is_dirty && <StatusBadge type="primary-green" text="CHANGED" />}
            {resourceData?.is_submitted && <StatusBadge type="hover-grey" text="SUBMITTED" />}
            {provisioningStateValue && (
                <StatusBadge
                    type={getCustomObjectProvisioningStateColors(provisioningStateValue)}
                    text={provisioningStateValue}
                />
            )}
            {getReadonlyBadges({ resourceData, resourceConfig })}
            {getDeletedBadges({ resourceData })}
            {getPendingTrashBadge({ resourceData })}
            {getPendingRestoreBadge({ resourceData })}
            {getPendingDeleteBadge({ resourceData })}
            {getDeprecationBadge({ resourceData })}
            {getSyntaxErrorBadge({ resourceData })}
            {getBundleContentBadge({ resourceData })}
            {getBootstrapUserBadge({ isBootstrapUser })}
            {resourceData &&
                (resourceName === "project" ||
                    resourceName === "bundle" ||
                    resourceName === "workspace") && (
                    <GitAction
                        data={resourceData}
                        isDisabled={isEmpty(resourceData) || isDisabled}
                        afterAction={fetchResourceData}
                        fetchSearchTabs={() => fetchSearchTabs?.()}
                        uncommittedData={uncommittedDataByPath}
                        isGitLocked={isGitLocked}
                    />
                )}
            {resourceData?.identity_role_plain_list_identity_id && (
                <RoleSelect
                    identity={resourceData}
                    onSelect={fetchResourceData}
                    onDeselect={fetchResourceData}
                    onUpdate={fetchResourceData}
                />
            )}
            {!resourceConfig?.actions?.hidePin && <PinPopover resourceData={resourceData} />}
            {resourceConfig?.statsSidebar && resourceData && (
                <Tooltip overlay="Latest Executions">
                    <BinaryStateButton
                        trueStateIcon={IconCollection.chartDonut}
                        falseStateIcon={IconCollection.chartDonut}
                        trueStateColor="primary"
                        state={Boolean(statsPopoverEl)}
                        onClick={(event) => {
                            setStatsPopoverEl(event.target);
                        }}
                        className="statsButton"
                        disabled={isEmpty(resourceData)}
                    />
                    <Popover
                        open={Boolean(statsPopoverEl)}
                        anchorEl={statsPopoverEl}
                        onClose={() => {
                            setStatsPopoverEl(null);
                        }}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                    >
                        <Box sx={{ p: 4 }}>
                            <ResourceStats
                                resourceData={resourceData}
                                resourceName={resourceName}
                            />
                        </Box>
                    </Popover>
                </Tooltip>
            )}
            {resourceName === "project" ||
            resourceName === "bundle" ||
            resourceName === "object_template" ||
            resourceName === "custom_object" ? (
                <Tooltip overlay={canvasType ? "Custom object canvas" : "Object template canvas"}>
                    <IconButton
                        component={Link}
                        to={canvasLink}
                        aria-label={canvasType ? "Custom object canvas" : "Object template canvas"}
                        disabled={isEmpty(resourceData)}
                    >
                        <Icon icon={IconCollection.vectorPolyLine} />
                    </IconButton>
                </Tooltip>
            ) : null}
            {(resourceName === "project" ||
                resourceName === "bundle" ||
                resourceName === "workspace") && (
                <Tooltip overlay="Access list">
                    <BinaryStateButton
                        trueStateIcon={IconCollection.security}
                        falseStateIcon={IconCollection.security}
                        trueStateColor="primary"
                        state={Boolean(accessListPopoverEl)}
                        onClick={(event) => {
                            // Anchor to the stable IconButton (currentTarget), NOT
                            // the clicked node (event.target = the inner <svg>/<path>
                            // of the icon). On open the button flips to its true-state
                            // icon, unmounting that <path>; a Popover anchored to the
                            // detached node repositions ("moves twice"). The button
                            // element persists across the icon swap, so the anchor and
                            // thus the popover position stay put.
                            setAccessListPopoverEl(event.currentTarget);
                        }}
                        className="accessListButton"
                        disabled={isEmpty(resourceData)}
                    />
                    <Popover
                        open={Boolean(accessListPopoverEl)}
                        anchorEl={accessListPopoverEl}
                        onClose={() => {
                            setAccessListPopoverEl(null);
                        }}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                        className="resourceSinglePageHeaderPopover"
                    >
                        <Box sx={{ p: 2 }}>
                            <AccessList />
                        </Box>
                    </Popover>
                </Tooltip>
            )}
            {resourceName !== "workspace" && resourceName !== "zone" && recordId && (
                <Tooltip overlay="Permission zones">
                    <BinaryStateButton
                        trueStateIcon={IconCollection.lockOutline}
                        falseStateIcon={IconCollection.lockOutline}
                        trueStateColor="primary"
                        state={Boolean(zonePopoverEl)}
                        // Anchor to currentTarget for the same reason as the access list.
                        onClick={(event) => setZonePopoverEl(event.currentTarget)}
                        className="zonesButton"
                        disabled={isEmpty(resourceData)}
                    />
                    <Popover
                        open={Boolean(zonePopoverEl)}
                        anchorEl={zonePopoverEl}
                        onClose={() => setZonePopoverEl(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        className="resourceSinglePageHeaderPopover"
                    >
                        <Box sx={{ p: 2 }}>
                            <ZoneTagList />
                        </Box>
                    </Popover>
                </Tooltip>
            )}
            {onRefresh && (
                <Tooltip overlay="Refresh — re-fetch this record from the server">
                    <IconButton
                        className={
                            isRefreshing ? "refreshButton refreshButton--spinning" : "refreshButton"
                        }
                        disabled={isEmpty(resourceData) || isRefreshing}
                        onClick={() => onRefresh()}
                        aria-label="Refresh record"
                    >
                        <Icon icon={IconCollection.refresh} />
                    </IconButton>
                </Tooltip>
            )}
            <DetailsPopover
                resourceName={resourceName}
                resourceData={resourceData}
                isFetching={isFetching}
                isDisabled={isDisabled}
                resourceConfig={resourceConfig}
                isGitEnabled={isGitEnabled}
                iri={iri}
                fetchResourceData={fetchResourceData}
            />
            <HiddenActionsComponent
                pristine={pristine}
                resourceData={resourceData}
                resourceName={resourceName}
                resourceConfig={resourceConfig}
                fetchResourceData={fetchResourceData}
                setModalVisible={setModalVisible}
                applyAction={applyAction}
                hasTooltip
                afterAction={fetchResourceData}
                uncommittedDataByPath={uncommittedDataByPath}
                provisioningState={provisioningState}
                iri={iri}
            />
        </>
    );

    const getBreadcrumbs = (): BreadcrumbProps[] => {
        let containerBreadcrumbs: BreadcrumbProps[] = [];

        const getLabel = () => {
            const connectorType =
                resourceData?.connector_type ?? defaultValues?.connector_type ?? "";
            const connectorId = resourceData?.connector_id_connector;

            return (
                <div className="flex items-center gap-8">
                    <div>{`${resourceName} ${resourceData?.type || ""} ${connectorType}`}</div>
                    {connectorId && (
                        <RecordLabel
                            link={getRelativeUrl("connector", connectorId.id)}
                            name={connectorId.name}
                            icon={resourceByName.connector.icon}
                        />
                    )}
                </div>
            );
        };

        if (isContainerType(resourceName)) {
            containerBreadcrumbs = [];
        } else if (!isEmpty(resourceData)) {
            if (resourceData.deleted_at) {
                containerBreadcrumbs = [
                    {
                        label: <RecordLabel name="Trash" icon={IconCollection.deleteOutline} />,
                        link: mapScreensToPaths.trash,
                    },
                ];
            } else {
                const { containerType: locationType, containerData: recordLocation } =
                    getRecordContainer(resourceData);

                containerBreadcrumbs =
                    locationType && recordLocation
                        ? [
                              {
                                  label: (
                                      <RecordLabel
                                          name={recordLocation.name}
                                          image={recordLocation.icon}
                                          iconByType={locationType}
                                      />
                                  ),
                                  link: getRelativeUrl(locationType, recordLocation.id),
                              },
                          ]
                        : [];
            }
        }

        return [
            ...containerBreadcrumbs,
            {
                link: "",
                label: getLabel(),
            },
        ];
    };

    const NewRecordHeadline = () => {
        const headline = `create new ${resourceName}`.toUpperCase();
        return (
            <div className="newRecordHeadline">
                <span>{headline}</span>
                <DocsInfoTooltip
                    title="Read more"
                    urlPath={`/docs/${recordDocLinks[resourceName]}`}
                />
            </div>
        );
    };

    const pageHeaderProps = {
        additionalActions: pageHeaderAdditionalActions,
        title: pageHeaderTitle,
        headline: isNewRecord ? (
            <NewRecordHeadline />
        ) : docsUrlPath ? (
            <div className="recordHeadlineWithDocs">
                <Breadcrumbs breadcrumbs={getBreadcrumbs()} />
                <DocsInfoTooltip title="Read more" urlPath={docsUrlPath} />
            </div>
        ) : (
            <Breadcrumbs breadcrumbs={getBreadcrumbs()} />
        ),
        titleImage: editIcon ? pageHeaderIcon : "",
        titleIcon: editIcon
            ? null
            : (titleIcon ?? (resourceConfig?.icon || IconCollection.codeTags)),
        // The description is NOT part of the sticky header: it is rendered as a
        // normal-flow block BELOW the header (see the return) so it simply scrolls
        // up and out of view under the opaque sticky header — no animation, no
        // scroll-position jumps (the browser's native scroll-anchoring holds the
        // position steady now that nothing writes an above-the-fold height on
        // scroll). Keeping only the compact breadcrumb/title/actions header sticky
        // also keeps CommentsPanel's `.page-header-sticky` bottom-edge measurement
        // valid. See operator-task-fe-deep-link-to-comment and
        // operator-task-fe-description-sticky-collapse-scroll-out-no-animation.
        subtitle: "",
        // Pin the (now description-free) compact header from the very first pixel
        // of scroll (`isScrollNeeded`, known synchronously) so no header element
        // ever travels upward and snaps back. The collapsed-state affordances
        // (divider) are gated separately on the actually-scrolled signal
        // (`isSticky`) so they never show at rest.
        className: `page-header--form ${canEditTitle ? "" : "page-header--form__title-readonly"}  ${
            isScrollNeeded ? "page-header-sticky" : ""
        }${isSticky && isScrollNeeded ? " page-header--scrolled" : ""}`,
        backButtonLink,
    };

    return (
        <>
            {applyAction.current && (
                <UnsavedChangesModal
                    applyAction={applyAction.current}
                    saveContent={hasValidationErrors ? undefined : handleSubmit}
                    setModalVisible={setModalVisible}
                    isModalVisible={isModalVisible}
                    resetForm={resetForm}
                />
            )}
            <div ref={ref} />
            <PageHeader {...pageHeaderProps}>{children}</PageHeader>
            {editDescription && pageHeaderSubtitle ? (
                // Record description as a normal-flow block below the sticky
                // header. It scrolls up under the opaque sticky header as the user
                // scrolls — no animation, no jump. The wrapper reuses the
                // `.page-header`/`.body` geometry so the description's left edge
                // sits at the header's base content padding, flush with the Save
                // button below it (and with the record title icon above it).
                <div className="page-header page-header--form resourceSingleDescription">
                    <div className="body">
                        <div className="page-header__title-headers">
                            <div className="page-header__subtitle">{pageHeaderSubtitle}</div>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default ResourceSinglePageHeader;
